import { useCallback } from 'react';
import AnalyticsService from '../services/AnalyticsService';
import { useAppSelector } from '../slices/store';
import { AnalyticsEventEnum } from '../types';
import { getBaseOnboardingProperties } from '../utils/OnboardingAnalyticsUtils';

// Onboarding Analytics Event Properties
export interface BaseOnboardingEventProperties {
  applicant_id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email_address: string;
  timestamp: string;
  user_platform: 'onereal';
  button_text?: string;
  session_id: string;
  screen_name: string;
  device_category: string;
  page_url: string;
}

// Partial user data from registration form (before account creation)
export interface PartialUserData {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  username?: string;
}

export interface OnboardingPaymentEventProperties
  extends BaseOnboardingEventProperties {
  fee_waived: boolean;
}

// Invited user role type for self-serve team onboarding
export type InvitedUserRole = 'team member';

// Team invite properties for self-serve onboarding
export interface TeamInviteProperties {
  invite_method: 'email' | 'link';
  team_invite_id: string;
  team_id: string;
  team_size?: number;
  team_region?: string;
}

// Self-serve team invite clicked event properties
export interface SelfServeTeamInviteClickedProperties
  extends BaseOnboardingEventProperties,
    TeamInviteProperties {}

// Self-serve onboarding application started event properties
export interface SelfServeOnboardingApplicationStartedProperties
  extends BaseOnboardingEventProperties,
    TeamInviteProperties {
  invited_user_role: InvitedUserRole;
}

// Self-serve onboarding application form submitted event properties
export interface SelfServeOnboardingApplicationFormSubmittedProperties
  extends BaseOnboardingEventProperties,
    TeamInviteProperties {
  invited_user_role: InvitedUserRole;
}

// Self-serve onboarding team addendum signed event properties
export interface SelfServeOnboardingTeamAddendumSignedProperties
  extends BaseOnboardingEventProperties,
    TeamInviteProperties {
  invited_user_role: InvitedUserRole;
}

/**
 * Custom hook for tracking onboarding milestone events
 * Provides type-safe methods for firing GA events with standardized properties
 */
export const useOnboardingTracking = () => {
  const { authUserDetail } = useAppSelector((state) => state.auth);

  /**
   * Internal helper to track an event with base properties
   * Tracks events even when user is not authenticated (e.g., during registration)
   */
  const trackEvent = useCallback(
    (
      eventName: AnalyticsEventEnum,
      screenName: string,
      additionalProperties?: Record<string, any>,
      buttonText?: string,
      partialUserData?: PartialUserData,
    ) => {
      // Get base properties - handles null/undefined user gracefully
      // If partialUserData is provided (e.g., from registration form), it will be used
      const baseProperties = getBaseOnboardingProperties(
        authUserDetail,
        screenName,
        buttonText,
        partialUserData,
      );

      AnalyticsService.instance().logEvent(eventName, {
        ...baseProperties,
        ...additionalProperties,
      });
    },
    [authUserDetail],
  );

  /**
   * Track when user starts the application (creates account)
   * @param screenName Current screen name (e.g., 'signup_screen')
   * @param buttonText Text of the button clicked (e.g., 'Create Account')
   * @param partialUserData Optional form data (firstName, lastName, email) from registration
   */
  const trackApplicationStarted = useCallback(
    (
      screenName: string,
      buttonText?: string,
      partialUserData?: PartialUserData,
    ) =>
      trackEvent(
        AnalyticsEventEnum.ONBOARDING_APPLICATION_STARTED,
        screenName,
        {},
        buttonText,
        partialUserData,
      ),
    [trackEvent],
  );

  /**
   * Track when user completes the application form section
   * @param screenName Current screen name
   * @param buttonText Text of the button clicked (e.g., 'Continue')
   */
  const trackApplicationFormSubmitted = useCallback(
    (screenName: string, buttonText?: string) =>
      trackEvent(
        AnalyticsEventEnum.ONBOARDING_APPLICATION_FORM_SUBMITTED,
        screenName,
        {},
        buttonText,
      ),
    [trackEvent],
  );

  /**
   * Track when user signs the ICA agreement
   * @param screenName Current screen name (e.g., 'ica_agreement_screen')
   * @param buttonText Text of the button clicked (e.g., 'Sign Agreement')
   */
  const trackICASigned = useCallback(
    (screenName: string, buttonText?: string) =>
      trackEvent(
        AnalyticsEventEnum.ONBOARDING_ICA_SIGNED,
        screenName,
        {},
        buttonText,
      ),
    [trackEvent],
  );

  /**
   * Track when user submits payment or payment is waived
   * @param screenName Current screen name (e.g., 'payment_screen')
   * @param feeWaived Whether the fee was waived (true/false)
   * @param buttonText Text of the button clicked (e.g., 'Submit Payment', 'Skip Payment')
   */
  const trackPaymentSubmitted = useCallback(
    (screenName: string, feeWaived: boolean, buttonText?: string) =>
      trackEvent(
        AnalyticsEventEnum.ONBOARDING_PAYMENT_SUBMITTED,
        screenName,
        { fee_waived: feeWaived },
        buttonText,
      ),
    [trackEvent],
  );

  /**
   * Track when user clicks on team invite link/email
   * @param screenName Current screen name
   * @param teamInviteProps Team invite properties (invite method, team info, etc.)
   * @param buttonText Optional button text
   * @param partialUserData Optional partial user data
   */
  const trackSelfServeTeamInviteClicked = useCallback(
    (
      screenName: string,
      teamInviteProps: TeamInviteProperties,
      buttonText?: string,
      partialUserData?: PartialUserData,
    ) =>
      trackEvent(
        AnalyticsEventEnum.SELF_SERVE_TEAM_INVITE_CLICKED,
        screenName,
        teamInviteProps,
        buttonText,
        partialUserData,
      ),
    [trackEvent],
  );

  /**
   * Track when user clicks 'create account' button in self-serve flow
   * @param screenName Current screen name
   * @param teamInviteProps Team invite properties (invite method, team info, etc.)
   * @param invitedUserRole Invited user role (team member or team_manager)
   * @param buttonText Optional button text
   * @param partialUserData Optional partial user data from form
   */
  const trackSelfServeOnboardingApplicationStarted = useCallback(
    (
      screenName: string,
      teamInviteProps: TeamInviteProperties,
      invitedUserRole: InvitedUserRole,
      buttonText?: string,
      partialUserData?: PartialUserData,
    ) =>
      trackEvent(
        AnalyticsEventEnum.SELF_SERVE_ONBOARDING_APPLICATION_STARTED,
        screenName,
        {
          ...teamInviteProps,
          invited_user_role: invitedUserRole,
          user_id: authUserDetail?.id || 'unknown',
        },
        buttonText,
        partialUserData,
      ),
    [trackEvent, authUserDetail],
  );

  /**
   * Track when user completes onboarding application form in self-serve flow
   * @param screenName Current screen name
   * @param teamInviteProps Team invite properties (invite method, team info, etc.)
   * @param invitedUserRole Invited user role (team member or team_manager)
   * @param buttonText Optional button text
   */
  const trackSelfServeOnboardingApplicationFormSubmitted = useCallback(
    (
      screenName: string,
      teamInviteProps: TeamInviteProperties,
      invitedUserRole: InvitedUserRole,
      buttonText?: string,
    ) =>
      trackEvent(
        AnalyticsEventEnum.SELF_SERVE_ONBOARDING_APPLICATION_FORM_SUBMITTED,
        screenName,
        {
          ...teamInviteProps,
          invited_user_role: invitedUserRole,
        },
        buttonText,
      ),
    [trackEvent],
  );

  /**
   * Track when user completes signing team addendum in self-serve flow
   * @param screenName Current screen name
   * @param teamInviteProps Team invite properties (invite method, team info, etc.)
   * @param invitedUserRole Invited user role (team member or team_manager)
   * @param buttonText Optional button text
   */
  const trackSelfServeOnboardingTeamAddendumSigned = useCallback(
    (
      screenName: string,
      teamInviteProps: TeamInviteProperties,
      invitedUserRole: InvitedUserRole,
      buttonText?: string,
    ) =>
      trackEvent(
        AnalyticsEventEnum.SELF_SERVE_ONBOARDING_TEAM_ADDENDUM_SIGNED,
        screenName,
        {
          ...teamInviteProps,
          invited_user_role: invitedUserRole,
        },
        buttonText,
      ),
    [trackEvent],
  );

  return {
    trackApplicationStarted,
    trackApplicationFormSubmitted,
    trackICASigned,
    trackPaymentSubmitted,
    // Self-serve team onboarding tracking methods
    trackSelfServeTeamInviteClicked,
    trackSelfServeOnboardingApplicationStarted,
    trackSelfServeOnboardingApplicationFormSubmitted,
    trackSelfServeOnboardingTeamAddendumSigned,
  };
};
